import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { MapPin, Phone, Clock, ExternalLink, ArrowUpRight, ArrowRight } from "lucide-react";

const LocationsGrid = ({ locations = [], showAll = false }) => {
  const { t, lang } = useLang();
  // Pe homepage: sediul central + primele 2 non-main. Pe /locatii: toate.
  const list = showAll
    ? locations
    : [
        ...locations.filter((l) => l.is_main).slice(0, 1),
        ...locations.filter((l) => !l.is_main).slice(0, 2),
      ];

  return (
    <section
      id="locations"
      className="bg-white py-20 lg:py-28"
      data-testid="locations-section"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-overline text-coral mb-4">
              {t.locations.kicker}
            </div>
            <h2 className="font-display font-black tracking-tight text-4xl lg:text-5xl text-burgundy">
              {t.locations.title}
            </h2>
            <p className="mt-4 font-mono-num text-sm text-black/60">
              {t.locations.subtitle}
            </p>
          </div>
          {!showAll && (
            <div className="lg:col-span-5 hidden lg:flex justify-end">
              <Link
                to="/locatii"
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#5a1a14]/25 text-xs uppercase tracking-overline text-burgundy hover:border-coral hover:text-coral transition-colors"
              >
                {lang === "en" ? "All branches" : "Toate punctele de lucru"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((loc, i) => (
            <article
              key={loc.id}
              className={`group border border-[#5a1a14]/15 hover:border-coral transition-all bg-white overflow-hidden flex flex-col ${
                loc.is_main ? "lg:col-span-2 lg:row-span-2" : ""
              }`}
              data-testid={`location-card-${loc.id}`}
            >
              {loc.image_url && (
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={loc.image_url}
                    alt={lang === "en" ? loc.name_en : loc.name_ro}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  {loc.is_main && (
                    <div className="absolute top-4 left-4 bg-coral text-white text-[10px] uppercase tracking-overline px-3 py-1.5">
                      {t.contact.hq}
                    </div>
                  )}
                  <span className="absolute top-4 right-4 font-mono-num text-xs bg-burgundy text-white px-2 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <Link
                  to={`/locatii/${loc.slug || loc.id}`}
                  className="font-display font-bold text-xl text-burgundy leading-tight hover:text-coral transition-colors inline-flex items-start gap-2 group/title"
                  data-testid={`location-link-${loc.slug || loc.id}`}
                >
                  <span>{lang === "en" ? loc.name_en : loc.name_ro}</span>
                  <ArrowUpRight className="w-4 h-4 mt-1 opacity-0 group-hover/title:opacity-100 transition-opacity shrink-0" />
                </Link>
                <ul className="mt-5 space-y-3 text-sm text-black/75 flex-1">
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-coral" />
                    <span>{lang === "en" ? loc.address_en : loc.address_ro}</span>
                  </li>
                  {loc.phone && (
                    <li className="flex items-start gap-3">
                      <Phone className="w-4 h-4 mt-0.5 shrink-0 text-coral" />
                      <span className="font-mono-num">{loc.phone}</span>
                    </li>
                  )}
                  <li className="flex items-start gap-3">
                    <Clock className="w-4 h-4 mt-0.5 shrink-0 text-coral" />
                    <span>{lang === "en" ? loc.hours_en : loc.hours_ro}</span>
                  </li>
                </ul>
                <div className="mt-6 flex items-center justify-between gap-4">
                  <Link
                    to={`/locatii/${loc.slug || loc.id}`}
                    className="text-[10px] uppercase tracking-overline text-coral hover:underline inline-flex items-center gap-1"
                    data-testid={`location-detail-${loc.slug || loc.id}`}
                  >
                    {lang === "en" ? "Open page" : "Vezi pagina"} <ArrowUpRight className="w-3 h-3" />
                  </Link>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      loc.maps_query || (lang === "en" ? loc.address_en : loc.address_ro) + " Romania"
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] uppercase tracking-overline text-black/50 hover:text-coral inline-flex items-center gap-1"
                    data-testid={`location-map-${loc.id}`}
                  >
                    {t.locations.cta_open} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>

        {!showAll && (
          <div className="mt-6 flex lg:hidden">
            <Link
              to="/locatii"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#5a1a14]/25 text-xs uppercase tracking-overline text-burgundy hover:border-coral hover:text-coral transition-colors w-full justify-center"
            >
              {lang === "en" ? "All branches" : "Toate punctele de lucru"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default LocationsGrid;

